@if (isLoading) {
  <section class="component-builder__loading py-5 d-flex justify-content-center">
    <ion-spinner name="crescent"></ion-spinner>
  </section>
} @else if (loadError) {
  <app-api-load-error
    title="Component builder unavailable"
    message="We couldn't load the component builder right now. Please try again."
    retryLabel="Retry"
    (retry)="loadBuilderData(true)"
  ></app-api-load-error>
} @else if (studioService.activeDefinition(); as definition) {
  <div class="component-builder container-fluid px-0 pt-0 pb-0">
    <nav class="component-builder__breadcrumb mb-2" aria-label="Component builder breadcrumb">
      <a class="component-builder__breadcrumb-link" routerLink="/component-library">Component library</a>
      <span class="component-builder__breadcrumb-separator">/</span>
      <span class="component-builder__breadcrumb-current">{{ definition.name }}</span>
    </nav>

    <section class="component-builder__surface component-builder__surface--top p-2" (contextmenu)="openDefinitionContextMenu($event)">
      <div class="component-builder__top-strip">
        <div class="component-builder__identity-inline">
          <label class="component-builder__field component-builder__field--name">
            <input
              class="component-builder__input component-builder__input--title"
              type="text"
              placeholder="Component name"
              [ngModel]="definition.name"
              (ngModelChange)="updateComponentName($event)"
            />
          </label>

          <label class="component-builder__field component-builder__field--description">
            <input
              class="component-builder__input component-builder__input--description"
              type="text"
              placeholder="Short description"
              [ngModel]="definition.description"
              (ngModelChange)="updateDescription($event)"
            />
          </label>
        </div>

        <div class="component-builder__meta-inline">
          <label class="component-builder__field component-builder__field--status">
            <select class="component-builder__select component-builder__select--compact" [ngModel]="definition.status" (ngModelChange)="updateStatus($event)">
              <option value="draft">Draft</option>
              <option value="ready">Ready</option>
            </select>
          </label>

          <div class="component-builder__meta-chips">
            <span class="component-builder__meta-chip" [class.is-custom]="definition.source === 'custom'">
              {{ definition.source === 'built-in' ? 'Built-in' : 'Custom' }}
            </span>
            <span class="component-builder__meta-chip">v{{ definition.version }}</span>
            <span class="component-builder__meta-chip">{{ definition.usageCount }} uses</span>
            <span class="component-builder__meta-chip">{{ definition.updatedAt | date: 'MMM d, y HH:mm' }}</span>
          </div>

          <div class="component-builder__action-row">
            <button type="button" class="component-builder__action-button" (click)="createNewComponent()">
              <ion-icon name="add-outline"></ion-icon>
              <span>New</span>
            </button>
            <button type="button" class="component-builder__action-button" (click)="duplicateCurrentComponent()">
              <ion-icon name="copy-outline"></ion-icon>
              <span>Duplicate</span>
            </button>
            <button type="button" class="component-builder__action-button is-primary" (click)="saveVersion()">
              <ion-icon name="save-outline"></ion-icon>
              <span>Save</span>
            </button>
          </div>
        </div>
      </div>

      <div class="component-builder__toolbar">
        <div class="component-builder__toolbar-group">
          <button type="button" class="component-builder__tool-button" [class.is-active]="showLayersDrawer" (click)="toggleLayersDrawer()">
            <ion-icon name="layers-outline"></ion-icon>
            <span>Layers</span>
          </button>
          <button type="button" class="component-builder__tool-button is-primary" (click)="openPresetModal()">
            <ion-icon name="sparkles-outline"></ion-icon>
            <span>Add preset</span>
          </button>
        </div>

        <div class="component-builder__toolbar-group">
          <div class="component-builder__toolbar-actions">
            <button type="button" class="component-builder__tool-button component-builder__tool-button--icon" title="Undo" [disabled]="!studioService.canUndo()" (click)="studioService.undo()">
              <ion-icon name="arrow-undo-outline"></ion-icon>
            </button>
            <button type="button" class="component-builder__tool-button component-builder__tool-button--icon" title="Redo" [disabled]="!studioService.canRedo()" (click)="studioService.redo()">
              <ion-icon name="arrow-redo-outline"></ion-icon>
            </button>
          </div>
        </div>

        <div class="component-builder__toolbar-group component-builder__toolbar-group--end">
          <div class="component-builder__theme-tools">
            <div class="component-builder__theme-select-wrap">
              <select
                class="component-builder__select component-builder__select--compact"
                [ngModel]="currentCanvasThemePackageId"
                (ngModelChange)="selectCanvasThemePackage($event)"
              >
                @for (themePackage of canvasThemeService.packages(); track themePackage.id) {
                  <option [value]="themePackage.id">{{ themePackage.name }}</option>
                }
              </select>
            </div>

            <label class="component-builder__theme-toggle" title="Toggle canvas theme">
              <ion-icon name="sunny-outline"></ion-icon>
              <input
                type="checkbox"
                [ngModel]="currentCanvasThemeMode === 'dark'"
                (ngModelChange)="setCanvasThemeMode($event ? 'dark' : 'light')"
              />
              <span class="component-builder__theme-toggle-track">
                <span class="component-builder__theme-toggle-thumb"></span>
              </span>
              <ion-icon name="moon-outline"></ion-icon>
            </label>

            <button type="button" class="component-builder__tool-button" title="Canvas themes" (click)="openThemeManagerModal()">
              <ion-icon name="color-palette-outline"></ion-icon>
              <span>Themes</span>
            </button>
          </div>
          <span class="component-builder__autosave-state" [class.is-saving]="isSavingBuilderState">
            {{
              isSavingBuilderState
                ? 'Saving...'
                : (hasPendingBuilderChanges ? 'Local draft pending' : 'All changes saved')
            }}
          </span>
        </div>
      </div>
    </section>

    <section class="component-builder__workspace mt-2" (contextmenu)="openWorkspaceContextMenu($event)">
      <div class="component-builder__workspace-body" [class.has-sidebar]="showLayersDrawer">
        @if (showLayersDrawer) {
          <aside class="component-builder__workspace-sidebar">
            <section class="component-builder__side-panel">
              <div class="component-builder__side-panel-header">
                <div>
                  <h3 class="component-builder__section-title mb-0 bw-title-with-info">Component tree<app-info-tooltip compact placement="right" text="The tree mirrors the component document. Select a layer to edit its content, layout, and style."></app-info-tooltip></h3>
                </div>

                <button type="button" class="component-builder__icon-button" title="Close layers" (click)="closeLayersDrawer()">
                  <ion-icon name="close-outline"></ion-icon>
                </button>
              </div>

              <div class="component-builder__side-panel-body">
                <section class="component-builder__side-panel-section">
                  <button type="button" class="component-builder__side-panel-section-toggle" [class.is-open]="layersPanelSections.selection" (click)="toggleLayersPanelSection('selection')">
                    <span>Selection</span>
                    <ion-icon name="chevron-forward-outline"></ion-icon>
                  </button>

                  @if (layersPanelSections.selection) {
                    <div class="component-builder__side-panel-section-body">
                      @if (selectedNode; as node) {
                        <div class="component-builder__selection-card">
                          <span class="component-builder__layer-label">{{ node.label }}</span>
                          <span class="component-builder__layer-type">{{ node.type | titlecase }}</span>
                          @if (isContainer(node)) {
                            <span class="component-builder__selection-meta">{{ node.children.length }} child{{ node.children.length === 1 ? '' : 'ren' }}</span>
                          }
                        </div>
                      } @else {
                        <div class="component-builder__selection-card">
                          <span class="component-builder__layer-label">{{ definition.root.label }}</span>
                          <span class="component-builder__layer-type">Root frame</span>
                        </div>
                      }
                    </div>
                  }
                </section>

                <section class="component-builder__side-panel-section">
                  <button type="button" class="component-builder__side-panel-section-toggle" [class.is-open]="layersPanelSections.tree" (click)="toggleLayersPanelSection('tree')">
                    <span>Structure</span>
                    <ion-icon name="chevron-forward-outline"></ion-icon>
                  </button>

                  @if (layersPanelSections.tree) {
                    <div class="component-builder__side-panel-section-body">
                      <div class="component-builder__layers">
                        <div class="component-builder__tree-node">
                          <div class="component-builder__tree-row is-root" [class.is-selected]="studioService.selectedNodeId() === definition.root.id">
                            @if (definition.root.children.length) {
                              <button type="button" class="component-builder__tree-toggle" [class.is-open]="isLayerExpanded(definition.root.id)" (click)="toggleLayerExpansion(definition.root.id, $event)" aria-label="Toggle root children">
                                <ion-icon name="chevron-forward-outline"></ion-icon>
                              </button>
                            } @else {
                              <span class="component-builder__tree-toggle-spacer" aria-hidden="true"></span>
                            }

                            <button
                              type="button"
                              class="component-builder__layer-button component-builder__layer-button--tree is-root"
                              (click)="selectLayerNode(definition.root.id, $event)"
                              (contextmenu)="openLayerContextMenu($event, definition.root)"
                            >
                              <ion-icon name="layers-outline"></ion-icon>
                              <div class="component-builder__layer-meta">
                                <span class="component-builder__layer-label">{{ definition.root.label }}</span>
                                <span class="component-builder__layer-type">Root frame</span>
                              </div>
                            </button>
                          </div>

                          @if (isLayerExpanded(definition.root.id)) {
                            <div class="component-builder__tree-children">
                              @for (child of definition.root.children; track child.id) {
                                <ng-container
                                  [ngTemplateOutlet]="layerNode"
                                  [ngTemplateOutletContext]="{ $implicit: child, depth: 1 }"
                                ></ng-container>
                              }
                            </div>
                          }
                        </div>
                      </div>
                    </div>
                  }
                </section>
              </div>
            </section>
          </aside>
        }

        <div class="component-builder__workspace-main">
          <div class="component-builder__viewport-toolbar">
            <div class="component-builder__viewport-mainbar">
              <div class="component-builder__viewport-mainbar-spacer"></div>

              <div class="component-builder__viewport-controls">
                <div class="component-builder__toolbar-meta">
                  <span class="component-builder__toolbar-label">Dimensions:</span>
                  <app-info-tooltip compact placement="bottom" text="Resize the component viewport like a browser device frame. Presets switch to common sizes and manual resizing changes the mode to Responsive."></app-info-tooltip>
                </div>

                <span
                  class="component-builder__viewport-control component-builder__viewport-control--mode"
                  [class.is-active]="previewMode === 'responsive'"
                >
                  Responsive
                </span>

                <label class="component-builder__viewport-input-wrap">
                  <input
                    class="component-builder__viewport-input"
                    type="number"
                    step="1"
                    [ngModel]="canvasWidth"
                    (ngModelChange)="updateViewportWidth($event)"
                  />
                </label>

                <span class="component-builder__viewport-separator">x</span>

                <label class="component-builder__viewport-input-wrap">
                  <input
                    class="component-builder__viewport-input"
                    type="number"
                    step="1"
                    [ngModel]="canvasHeight"
                    (ngModelChange)="updateViewportHeight($event)"
                  />
                </label>

                <div
                  class="component-builder__viewport-combo"
                  [class.is-open]="showZoomPresetMenu"
                  (focusout)="handleViewportZoomFocusOut($event)"
                >
                  <div class="component-builder__viewport-zoom-field">
                    <input
                      class="component-builder__viewport-input component-builder__viewport-input--zoom"
                      type="number"
                      step="1"
                      [ngModel]="canvasZoomPercentage"
                      (ngModelChange)="updateViewportZoom($event)"
                      (focus)="openViewportZoomMenu()"
                      (click)="openViewportZoomMenu()"
                    />
                    <span class="component-builder__viewport-suffix">%</span>
                    <div class="component-builder__viewport-stepper">
                      <button
                        type="button"
                        class="component-builder__viewport-stepper-button component-builder__viewport-stepper-button--up"
                        aria-label="Increase zoom"
                        (click)="nudgeViewportZoom(1)"
                      ></button>
                      <button
                        type="button"
                        class="component-builder__viewport-stepper-button component-builder__viewport-stepper-button--down"
                        aria-label="Decrease zoom"
                        (click)="nudgeViewportZoom(-1)"
                      ></button>
                    </div>
                  </div>

                  <div class="component-builder__viewport-combo-menu" role="listbox" aria-label="Zoom presets">
                    @for (zoomPreset of zoomPresetOptions; track zoomPreset) {
                      <button
                        type="button"
                        class="component-builder__viewport-combo-option"
                        (click)="selectViewportZoomPreset(zoomPreset)"
                      >
                        {{ zoomPreset }}%
                      </button>
                    }
                  </div>
                </div>
              </div>

              <div class="component-builder__viewport-actions">
                <label class="component-builder__viewport-check">
                  <input type="checkbox" [checked]="showViewportGrid" (change)="toggleViewportGrid()" />
                  <span>Grid</span>
                </label>
                <label class="component-builder__viewport-check">
                  <input type="checkbox" [checked]="showViewportRulers" (change)="toggleViewportRulers()" />
                  <span>Rulers</span>
                </label>
                <button type="button" class="component-builder__viewport-control component-builder__viewport-control--preview" (click)="openPreviewModal()">
                  <ion-icon name="eye-outline"></ion-icon>
                  <span>Preview</span>
                </button>
              </div>
            </div>

            <div class="component-builder__viewport-presetsbar" role="toolbar" aria-label="Viewport presets">
              @for (preset of previewPresets; track preset.id) {
                <button
                  type="button"
                  class="component-builder__viewport-preset"
                  [class.is-active]="previewMode === preset.id"
                  [title]="preset.label"
                  (click)="setPreviewMode(preset.id)"
                >
                  {{ preset.label }}
                </button>
              }
            </div>
          </div>

          <div class="component-builder__canvas-shell p-2" #canvasViewportShell [ngStyle]="canvasShellStyle">
            <div class="component-builder__canvas-stage">
              <div class="component-builder__canvas-viewport-frame" [ngStyle]="canvasViewportFrameStyle">
            @if (showViewportRulers) {
              <div class="component-builder__viewport-ruler-corner"></div>
              <canvas
                class="component-builder__viewport-ruler-canvas component-builder__viewport-ruler-canvas--top"
                [ngStyle]="topRulerCanvasStyle"
                aria-hidden="true"
              ></canvas>
              <canvas
                class="component-builder__viewport-ruler-canvas component-builder__viewport-ruler-canvas--left"
                [ngStyle]="leftRulerCanvasStyle"
                aria-hidden="true"
              ></canvas>
            }

            @if (showViewportGrid) {
              <canvas class="component-builder__viewport-grid" [ngStyle]="gridStyle" aria-hidden="true"></canvas>
            }

            <div class="component-builder__canvas-viewport" [ngStyle]="scaledCanvasViewportStyle" (scroll)="handleCanvasViewportScroll($event)">
              <div
                class="component-builder__canvas-root"
                [class.is-selected]="studioService.selectedNodeId() === definition.root.id"
                [ngStyle]="nodeStyles(definition.root)"
                (click)="selectNode(definition.root.id, $event)"
                (contextmenu)="openCanvasContextMenu($event, definition.root)"
              >
                <div class="component-builder__root-header">
                  <div>
                    <h3 class="component-builder__root-title mb-0 bw-title-with-info">
                      {{ definition.name }}
                      <app-info-tooltip compact placement="left" text="This is the root container for the reusable component. Insert content here and select any node to edit it in the inspector."></app-info-tooltip>
                    </h3>
                  </div>

                  <button type="button" class="component-builder__insert-inline" (click)="openPresetModal()">
                    <ion-icon name="add-outline"></ion-icon>
                    <span>Insert</span>
                  </button>
                </div>

                <div
                  class="component-builder__dropzone"
                  cdkDropList
                  [id]="dropListId(definition.root.id)"
                  [cdkDropListData]="definition.root.children"
                  [cdkDropListConnectedTo]="connectedDropListIds"
                  (cdkDropListDropped)="handleNodeDrop($event, definition.root.id)"
                >
                  @if (!definition.root.children.length) {
                    <div class="component-builder__empty-drop">
                      Add a preset or drop nested blocks here to start composing the component.
                    </div>
                  }

                  @for (child of definition.root.children; track child.id) {
                    <ng-container
                      [ngTemplateOutlet]="canvasNode"
                      [ngTemplateOutletContext]="{ $implicit: child, parentId: definition.root.id }"
                    ></ng-container>
                  }
                </div>
              </div>
            </div>

            <button
              type="button"
              class="component-builder__canvas-resize-handle"
              aria-label="Resize canvas"
              title="Resize canvas"
              (pointerdown)="startViewportResize($event, 'se')"
              (dblclick)="maximizeViewport()"
            ></button>
            <button
              type="button"
              class="component-builder__canvas-resize-handle component-builder__canvas-resize-handle--bottom"
              aria-label="Resize canvas height"
              title="Resize canvas height"
              (pointerdown)="startViewportResize($event, 's')"
              (dblclick)="maximizeViewportHeight()"
            ></button>
            <button
              type="button"
              class="component-builder__canvas-resize-handle component-builder__canvas-resize-handle--right"
              aria-label="Resize canvas width"
              title="Resize canvas width"
              (pointerdown)="startViewportResize($event, 'e')"
              (dblclick)="maximizeViewportWidth()"
            ></button>
            <button
              type="button"
              class="component-builder__canvas-resize-handle component-builder__canvas-resize-handle--left"
              aria-label="Resize canvas from left"
              title="Resize canvas from left"
              (pointerdown)="startViewportResize($event, 'w')"
              (dblclick)="maximizeViewportWidth()"
            ></button>
            <button
              type="button"
              class="component-builder__canvas-resize-handle component-builder__canvas-resize-handle--corner-left"
              aria-label="Resize canvas from bottom left"
              title="Resize canvas from bottom left"
              (pointerdown)="startViewportResize($event, 'sw')"
              (dblclick)="maximizeViewport()"
            ></button>
          </div>
        </div>
      </div>
      </div>
    </div>
    </section>

    @if (showPresetModal) {
      <div class="component-builder__modal-layer">
        <button type="button" class="component-builder__modal-scrim" aria-label="Close presets" (click)="closePresetModal()"></button>

        <section class="component-builder__modal">
          <div class="component-builder__modal-header">
            <div>
              <h3 class="component-builder__section-title mb-0 bw-title-with-info">Add preset<app-info-tooltip compact placement="right" text="Presets are editable starter structures inserted at the current target."></app-info-tooltip></h3>
            </div>

            <button type="button" class="component-builder__icon-button" title="Close presets" (click)="closePresetModal()">
              <ion-icon name="close-outline"></ion-icon>
            </button>
          </div>

          <div class="component-builder__preset-grid mt-3">
            @for (preset of studioService.presets; track preset.id) {
              <button
                type="button"
                class="component-builder__preset-card"
                [style.--bw-preset-accent]="preset.accent"
                (click)="insertPreset(preset.id)"
                (contextmenu)="openPresetContextMenu($event, preset)"
              >
                <div class="component-builder__preset-icon-wrap">
                  <ion-icon [name]="preset.icon"></ion-icon>
                </div>
                <div class="component-builder__preset-content">
                  <span class="component-builder__preset-label">{{ preset.label }}</span>
                  <span class="component-builder__preset-copy">{{ preset.description }}</span>
                </div>
              </button>
            }
          </div>
        </section>
      </div>
    }

    @if (showThemeManagerModal) {
      <div class="component-builder__modal-layer">
        <button type="button" class="component-builder__modal-scrim" aria-label="Close canvas themes" (click)="closeThemeManagerModal()"></button>

        <section class="component-builder__modal component-builder__modal--wide">
          <div class="component-builder__modal-header">
            <div>
              <h3 class="component-builder__section-title mb-0 bw-title-with-info">Manage theme packages<app-info-tooltip compact placement="right" text="Create theme packages to override selected canvas colours without changing the default package."></app-info-tooltip></h3>
            </div>

            <div class="component-builder__modal-header-actions">
              <button type="button" class="component-builder__action-button" (click)="createThemePackage()">
                <ion-icon name="add-outline"></ion-icon>
                <span>New package</span>
              </button>
              <button type="button" class="component-builder__icon-button" title="Close themes" (click)="closeThemeManagerModal()">
                <ion-icon name="close-outline"></ion-icon>
              </button>
            </div>
          </div>

          <div class="component-builder__theme-manager mt-3">
            <aside class="component-builder__theme-package-list">
              @for (themePackage of canvasThemeService.packages(); track themePackage.id) {
                <button
                  type="button"
                  class="component-builder__theme-package-button"
                  [class.is-selected]="editingThemePackageId === themePackage.id"
                  (click)="chooseThemeDraft(themePackage.id)"
                >
                  <span class="component-builder__theme-package-name">{{ themePackage.name }}</span>
                  <span class="component-builder__theme-package-copy">{{ themePackage.description || 'Inherits defaults until overrides are added.' }}</span>
                </button>
              }
            </aside>

            <div class="component-builder__theme-editor">
              @if (themeDraft; as draft) {
                @if (draft.id === 'default') {
                  <section class="component-builder__theme-default-note">
                    <h4 class="component-builder__section-title mb-3 bw-title-with-info">Default preview colors<app-info-tooltip compact placement="right" text="The default package mirrors builder preview defaults. Create a new package and override only the values you need."></app-info-tooltip></h4>
                    <button type="button" class="component-builder__action-button is-primary" (click)="createThemePackage()">
                      <ion-icon name="add-outline"></ion-icon>
                      <span>Create override package</span>
                    </button>
                  </section>
                } @else {
                  <div class="component-builder__theme-editor-grid">
                    <label class="component-builder__field">
                      <span class="component-builder__field-label">Package name</span>
                      <input class="component-builder__input" type="text" [ngModel]="draft.name" (ngModelChange)="updateThemeDraftMeta('name', $event)" />
                    </label>

                    <label class="component-builder__field">
                      <span class="component-builder__field-label">Description</span>
                      <input class="component-builder__input" type="text" [ngModel]="draft.description" (ngModelChange)="updateThemeDraftMeta('description', $event)" />
                    </label>
                  </div>

                  <div class="component-builder__theme-columns mt-3">
                    <section class="component-builder__theme-column">
                      <div class="component-builder__theme-column-header">
                        <ion-icon name="sunny-outline"></ion-icon>
                        <strong>Light overrides</strong>
                      </div>

                      <div class="component-builder__theme-fields">
                        @for (field of canvasThemeFields; track field.key) {
                          <label class="component-builder__field">
                            <span class="component-builder__field-label">{{ field.label }}</span>
                            <input
                              class="component-builder__input"
                              type="text"
                              [ngModel]="draft.lightOverrides[field.key] || ''"
                              (ngModelChange)="updateThemeDraftOverride('light', field.key, $event)"
                            />
                          </label>
                        }
                      </div>
                    </section>

                    <section class="component-builder__theme-column">
                      <div class="component-builder__theme-column-header">
                        <ion-icon name="moon-outline"></ion-icon>
                        <strong>Dark overrides</strong>
                      </div>

                      <div class="component-builder__theme-fields">
                        @for (field of canvasThemeFields; track field.key) {
                          <label class="component-builder__field">
                            <span class="component-builder__field-label">{{ field.label }}</span>
                            <input
                              class="component-builder__input"
                              type="text"
                              [ngModel]="draft.darkOverrides[field.key] || ''"
                              (ngModelChange)="updateThemeDraftOverride('dark', field.key, $event)"
                            />
                          </label>
                        }
                      </div>
                    </section>
                  </div>

                  <div class="component-builder__theme-actions mt-3">
                    <button type="button" class="component-builder__action-button is-primary" (click)="saveThemePackage()">
                      <ion-icon name="save-outline"></ion-icon>
                      <span>Save package</span>
                    </button>
                    <button type="button" class="component-builder__action-button" (click)="selectCanvasThemePackage(draft.id)">
                      <ion-icon name="radio-button-on-outline"></ion-icon>
                      <span>Use package</span>
                    </button>
                    <button type="button" class="component-builder__action-button component-builder__action-button--danger" (click)="deleteThemePackage()">
                      <ion-icon name="close-outline"></ion-icon>
                      <span>Delete</span>
                    </button>
                  </div>
                }
              }
            </div>
          </div>
        </section>
      </div>
    }

    @if (showPreviewModal) {
      <div class="component-builder__modal-layer">
        <button type="button" class="component-builder__modal-scrim" aria-label="Close preview" (click)="closePreviewModal()"></button>

        <section class="component-builder__modal component-builder__modal--wide component-builder__modal--preview">
          <div class="component-builder__modal-header">
            <div>
              <h3 class="component-builder__section-title mb-0 bw-title-with-info">{{ definition.name }} rendered preview<app-info-tooltip compact placement="right" text="Inspect the current component document before using it in a page."></app-info-tooltip></h3>
            </div>

            <div class="component-builder__modal-header-actions">
              <span class="component-builder__meta-chip">{{ currentCanvasThemeMode | titlecase }}</span>
              <span class="component-builder__meta-chip">{{ canvasWidth }} x {{ canvasHeight }}</span>
              <button type="button" class="component-builder__icon-button" title="Close preview" (click)="closePreviewModal()">
                <ion-icon name="close-outline"></ion-icon>
              </button>
            </div>
          </div>

          <div class="component-builder__preview-frame mt-3" [ngStyle]="canvasShellStyle">
            <div class="component-builder__preview-stage">
              <div class="component-builder__canvas-viewport is-preview" [ngStyle]="canvasViewportStyle">
                <div class="component-builder__preview-root" [ngStyle]="nodeStyles(definition.root)">
                  @for (child of definition.root.children; track child.id) {
                    <ng-container [ngTemplateOutlet]="previewNode" [ngTemplateOutletContext]="{ $implicit: child }"></ng-container>
                  }
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    }
  </div>

  <ng-template #canvasNode let-node let-parentId="parentId">
    <div
      class="component-builder__node"
      [class.is-selected]="studioService.selectedNodeId() === node.id"
      [class.is-hidden]="node.hidden"
      [class.is-absolute]="node.style.position === 'absolute'"
      [attr.data-node-id]="node.id"
      cdkDrag
      [cdkDragData]="node.id"
      [cdkDragDisabled]="node.locked"
      (click)="selectNode(node.id, $event)"
      (contextmenu)="openCanvasContextMenu($event, node)"
    >
      <div class="component-builder__node-toolbar">
        <div class="component-builder__node-tag">
          <ion-icon [name]="nodeIcon(node.type)"></ion-icon>
          <span>{{ node.label }}</span>
        </div>
        @if (isContainer(node)) {
          <button type="button" class="component-builder__insert-inline" (click)="openPresetModal(); selectNode(node.id, $event)">
            <ion-icon name="add-outline"></ion-icon>
            <span>Insert</span>
          </button>
        }
      </div>

      <div class="component-builder__node-box" [ngStyle]="nodeStyles(node)">
        @switch (node.type) {
          @case ('text') {
            <div class="component-builder__text-node">
              @if (node.content.eyebrow) {
                <p class="component-builder__node-eyebrow mb-1">{{ node.content.eyebrow }}</p>
              }
              @if (node.content.title) {
                <h4 class="component-builder__node-heading mb-2">{{ node.content.title }}</h4>
              }
              @if (node.content.text) {
                <p class="component-builder__node-copy mb-0">{{ node.content.text }}</p>
              }
              @if (node.content.secondaryText) {
                <p class="component-builder__node-copy is-secondary mb-0">{{ node.content.secondaryText }}</p>
              }
            </div>
          }
          @case ('button') {
            <button type="button" class="component-builder__button-node">{{ node.content.buttonText || node.content.title || 'Button' }}</button>
          }
          @case ('media') {
            <div class="component-builder__media-node">
              <span class="component-builder__media-label">{{ node.content.mediaLabel || 'Media' }}</span>
              @if (node.content.caption) {
                <span class="component-builder__media-caption">{{ node.content.caption }}</span>
              }
            </div>
          }
          @case ('badge') {
            <span class="component-builder__badge-node">{{ node.content.badgeText || node.content.title || 'Badge' }}</span>
          }
        }

        @if (isContainer(node)) {
          <div
            class="component-builder__dropzone is-nested"
            cdkDropList
            [id]="dropListId(node.id)"
            [cdkDropListData]="node.children"
            [cdkDropListConnectedTo]="connectedDropListIds"
            (cdkDropListDropped)="handleNodeDrop($event, node.id)"
          >
            @if (!node.children.length) {
              <div class="component-builder__empty-drop is-inline">Drop nested blocks here</div>
            }
            @for (child of node.children; track child.id) {
              <ng-container
                [ngTemplateOutlet]="canvasNode"
                [ngTemplateOutletContext]="{ $implicit: child, parentId: node.id }"
              ></ng-container>
            }
          </div>
        }
      </div>
    </div>
  </ng-template>

  <ng-template #layerNode let-node let-depth="depth">
    <div class="component-builder__tree-node">
      <div class="component-builder__tree-row" [class.is-selected]="studioService.selectedNodeId() === node.id" [style.paddingInlineStart]="layerPadding(depth)">
        @if (hasLayerChildren(node)) {
          <button type="button" class="component-builder__tree-toggle" [class.is-open]="isLayerExpanded(node.id)" (click)="toggleLayerExpansion(node.id, $event)" aria-label="Toggle child layers">
            <ion-icon name="chevron-forward-outline"></ion-icon>
          </button>
        } @else {
          <span class="component-builder__tree-toggle-spacer" aria-hidden="true"></span>
        }

        <button
          type="button"
          class="component-builder__layer-button component-builder__layer-button--tree"
          (click)="selectLayerNode(node.id, $event)"
          (contextmenu)="openLayerContextMenu($event, node)"
        >
          <ion-icon [name]="nodeIcon(node.type)"></ion-icon>
          <div class="component-builder__layer-meta">
            <span class="component-builder__layer-label">{{ node.label }}</span>
            <span class="component-builder__layer-type">{{ node.type | titlecase }}</span>
          </div>
        </button>
      </div>

      @if (hasLayerChildren(node) && isLayerExpanded(node.id)) {
        <div class="component-builder__tree-children">
          @for (child of node.children; track child.id) {
            <ng-container
              [ngTemplateOutlet]="layerNode"
              [ngTemplateOutletContext]="{ $implicit: child, depth: depth + 1 }"
            ></ng-container>
          }
        </div>
      }
    </div>
  </ng-template>

  <ng-template #previewNode let-node>
    <div class="component-builder__preview-node" [ngStyle]="nodeStyles(node)">
      @switch (node.type) {
        @case ('text') {
          <div class="component-builder__text-node">
            @if (node.content.eyebrow) {
              <p class="component-builder__node-eyebrow mb-1">{{ node.content.eyebrow }}</p>
            }
            @if (node.content.title) {
              <h4 class="component-builder__node-heading mb-2">{{ node.content.title }}</h4>
            }
            @if (node.content.text) {
              <p class="component-builder__node-copy mb-0">{{ node.content.text }}</p>
            }
            @if (node.content.secondaryText) {
              <p class="component-builder__node-copy is-secondary mb-0">{{ node.content.secondaryText }}</p>
            }
          </div>
        }
        @case ('button') {
          <button type="button" class="component-builder__button-node">{{ node.content.buttonText || node.content.title || 'Button' }}</button>
        }
        @case ('media') {
          <div class="component-builder__media-node">
            <span class="component-builder__media-label">{{ node.content.mediaLabel || 'Media' }}</span>
            @if (node.content.caption) {
              <span class="component-builder__media-caption">{{ node.content.caption }}</span>
            }
          </div>
        }
        @case ('badge') {
          <span class="component-builder__badge-node">{{ node.content.badgeText || node.content.title || 'Badge' }}</span>
        }
      }

      @if (isContainer(node) && node.children.length) {
        @for (child of node.children; track child.id) {
          <ng-container [ngTemplateOutlet]="previewNode" [ngTemplateOutletContext]="{ $implicit: child }"></ng-container>
        }
      }
    </div>
  </ng-template>
} @else {
  <div class="component-builder__empty-state">
    <h2 class="component-builder__title mb-0 bw-title-with-info">No component selected<app-info-tooltip compact placement="right" text="Create a new component from the library or start a blank one here."></app-info-tooltip></h2>
  </div>
}
